import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// NOTE: Token is stored in localStorage for simplicity. 
// For production with stricter security requirements, consider:
// - httpOnly cookies with CSRF protection
// - Server-side session management
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Products
  getProducts: () => axios.get(`${API}/products`, { headers: getAuthHeader() }),
  getProduct: (id) => axios.get(`${API}/products/${id}`, { headers: getAuthHeader() }),
  getProductByBarcode: (barcode) => axios.get(`${API}/products/barcode/${barcode}`, { headers: getAuthHeader() }),
  createProduct: (data) => axios.post(`${API}/products`, data, { headers: getAuthHeader() }),
  updateProduct: (id, data) => axios.put(`${API}/products/${id}`, data, { headers: getAuthHeader() }),
  deleteProduct: (id) => axios.delete(`${API}/products/${id}`, { headers: getAuthHeader() }),
  adjustProductStock: (id, data) => axios.put(`${API}/products/${id}/stock`, data, { headers: getAuthHeader() }),

  // Movements
  getMovements: () => axios.get(`${API}/movements`, { headers: getAuthHeader() }),
  getProductMovements: (productId) => axios.get(`${API}/movements/product/${productId}`, { headers: getAuthHeader() }),
  createMovement: (data) => axios.post(`${API}/movements`, data, { headers: getAuthHeader() }),

  // Suppliers
  getSuppliers: () => axios.get(`${API}/suppliers`, { headers: getAuthHeader() }),
  createSupplier: (data) => axios.post(`${API}/suppliers`, data, { headers: getAuthHeader() }),
  updateSupplier: (id, data) => axios.put(`${API}/suppliers/${id}`, data, { headers: getAuthHeader() }),
  deleteSupplier: (id) => axios.delete(`${API}/suppliers/${id}`, { headers: getAuthHeader() }),

  // Customers
  getCustomers: () => axios.get(`${API}/customers`, { headers: getAuthHeader() }),
  createCustomer: (data) => axios.post(`${API}/customers`, data, { headers: getAuthHeader() }),
  updateCustomer: (id, data) => axios.put(`${API}/customers/${id}`, data, { headers: getAuthHeader() }),
  deleteCustomer: (id) => axios.delete(`${API}/customers/${id}`, { headers: getAuthHeader() }),

  // Events
  getEventDetails: (eventId) => axios.get(`${API}/events/${eventId}`, { headers: getAuthHeader() }),
  getEventSummary: (eventId) => axios.get(`${API}/events/${eventId}/summary`, { headers: getAuthHeader() }),
  getEventMovements: (eventId) => axios.get(`${API}/events/${eventId}/movements`, { headers: getAuthHeader() }),
  getActiveEvents: () => axios.get(`${API}/events/active/list`, { headers: getAuthHeader() }),
  updateEventStatus: (eventId, status) => axios.patch(`${API}/events/${eventId}/status`, { status }, { headers: getAuthHeader() }),
  exportEventToExcel: (eventId) => axios.get(`${API}/events/${eventId}/export-excel`, { headers: getAuthHeader(), responseType: 'blob' }),

  // Delivery Notes
  getDeliveryNotes: () => axios.get(`${API}/delivery-notes`, { headers: getAuthHeader() }),
  getDeliveryNote: (id) => axios.get(`${API}/delivery-notes/${id}`, { headers: getAuthHeader() }),
  createDeliveryNote: (data) => axios.post(`${API}/delivery-notes`, data, { headers: getAuthHeader() }),
  deleteDeliveryNote: (id) => axios.delete(`${API}/delivery-notes/${id}`, { headers: getAuthHeader() }),
  updateDeliveryNoteFilename: (id, filename) => axios.put(`${API}/delivery-notes/${id}/filename`, { custom_filename: filename }, { headers: getAuthHeader() }),
  getDeliveryNotePDF: (id) => axios.get(`${API}/delivery-notes/${id}/pdf`, { headers: getAuthHeader(), responseType: 'blob' }),
  getDeliveryNoteExcel: (id) => axios.get(`${API}/delivery-notes/${id}/excel`, { headers: getAuthHeader(), responseType: 'blob' }),

  // Dashboard
  getDashboardStats: () => axios.get(`${API}/dashboard/stats`, { headers: getAuthHeader() }),

  // Export/Import
  exportProducts: () => axios.get(`${API}/export/products`, { headers: getAuthHeader(), responseType: 'blob' }),
  importProducts: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/import/products`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },

  // Companies (super admin only)
  getCompanies: () => axios.get(`${API}/companies`, { headers: getAuthHeader() }),
  createCompany: (data) => axios.post(`${API}/companies`, data, { headers: getAuthHeader() }),

  // Company/Fiscal Data
  getMyCompanyData: () => axios.get(`${API}/users/me/company`, { headers: getAuthHeader() }),
  updateMyCompanyData: (data) => axios.put(`${API}/users/me/company`, data, { headers: getAuthHeader() }),
  uploadCompanyLogo: (logo_base64) => axios.post(`${API}/users/me/company/logo`, { logo_base64 }, { headers: getAuthHeader() }),
  deleteCompanyLogo: () => axios.delete(`${API}/users/me/company/logo`, { headers: getAuthHeader() }),

  // User Management (admin only)
  getAllUsers: () => axios.get(`${API}/users`, { headers: getAuthHeader() }),
  createUser: (data) => axios.post(`${API}/users`, data, { headers: getAuthHeader() }),
  deleteUser: (userId) => axios.delete(`${API}/users/${userId}`, { headers: getAuthHeader() }),
  changeUserPassword: (userId, newPassword) => axios.put(`${API}/users/${userId}/password`, { new_password: newPassword }, { headers: getAuthHeader() }),

  // Operator Management
  getOperators: () => axios.get(`${API}/operators`, { headers: getAuthHeader() }),
  createOperator: (data) => axios.post(`${API}/operators`, data, { headers: getAuthHeader() }),
  deleteOperator: (operatorId) => axios.delete(`${API}/operators/${operatorId}`, { headers: getAuthHeader() }),
};